import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Printer, Eye, Search, Settings2, Save, Plus, X as XIcon, GraduationCap, Trophy } from "lucide-react";
import { toast } from "sonner";
import "./report-card.css";

const DEFAULT_TERMS = ["PT-1", "Half-Yearly", "PT-2", "Annual"];
const DEFAULT_SCHOLASTIC = [
  { min: 91, max: 100, grade: "A1" }, { min: 81, max: 90, grade: "A2" },
  { min: 71, max: 80, grade: "B1" }, { min: 61, max: 70, grade: "B2" },
  { min: 51, max: 60, grade: "C1" }, { min: 41, max: 50, grade: "C2" },
  { min: 33, max: 40, grade: "D" }, { min: 0, max: 32, grade: "E" },
];
const DEFAULT_CO_AREAS = ["Work Education", "Art Education", "Health & Physical Education", "Discipline"];

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
const gradeFromRanges = (pct, ranges) => {
  for (const r of ranges || []) {
    const lo = num(r.min) ?? 0, hi = num(r.max) ?? 100;
    if (pct >= lo && pct <= hi) return r.grade || "";
  }
  return "";
};
const pickRange = (pct, ranges, key) => {
  for (const r of ranges || []) {
    const lo = num(r.min) ?? 0, hi = num(r.max) ?? 100;
    if (pct >= lo && pct <= hi) return r[key] || "";
  }
  return "";
};

function recalc(rpt, terms, grades) {
  const c = structuredClone(rpt);
  let gO = 0, gM = 0;
  (c.subjects || []).forEach((s) => {
    let obt = 0, max = 0, any = false;
    terms.forEach((lbl) => {
      const t = (s.terms && s.terms[lbl]) || {};
      const o = num(t.obtained), m = num(t.max);
      if (m != null) max += m;
      if (o != null) { obt += o; any = true; }
    });
    s.total_obtained = Math.round(obt * 100) / 100;
    s.total_max = Math.round(max * 100) / 100;
    s.percentage = max ? Math.round((obt / max) * 10000) / 100 : 0;
    s.grade = any ? gradeFromRanges(s.percentage, grades) : "";
    gO += obt; gM += max;
  });
  c.total_obtained = Math.round(gO * 100) / 100;
  c.total_max = Math.round(gM * 100) / 100;
  c.percentage = gM ? Math.round((gO / gM) * 10000) / 100 : 0;
  c.overall_grade = gradeFromRanges(c.percentage, grades);
  return c;
}

const IN = "border border-slate-300 rounded px-1 py-0.5 text-xs w-full text-center focus:outline-none focus:border-indigo-400 print:border-0";

function ProgressCardView({ rpt, terms, school, grades, coGrades, editable, update, adv, divisions, remarks }) {
  const st = rpt.student || {};
  const att = rpt.attendance || {};
  const wd = num(att.working_days), pr = num(att.present);
  const pct = wd ? Math.round(((pr ?? 0) / wd) * 1000) / 10 : 0;
  const labels = (adv && adv.labels) || {};
  const hide = (adv && adv.hide) || {};
  const style = (adv && adv.styling) || {};
  const L = (key, fallback) => labels[key] || fallback;
  const H = (key) => !!hide[key];
  const landscape = adv && adv.orientation === "Landscape";
  const division = pickRange(rpt.percentage, divisions, "name");
  const remark = pickRange(rpt.percentage, remarks, "remark");
  const headStyle = { background: style.header_bg || undefined, color: style.header_text || undefined };
  const theadStyle = { background: style.heading_bg || "#f1f5f9", color: style.heading_text || "#0f172a" };
  const showGrade = !H("Hide Final Grade Column");
  const showTotal = !H("Hide Total/Maximum Marks Column") && !H("Hide Obtained Marks Column");
  const field = (val, onChange, cls = "") => editable
    ? <input className={IN + " " + cls} value={val ?? ""} onChange={(e) => onChange(e.target.value)} />
    : <span>{val || ""}</span>;
  return (
    <div className="progress-card bg-white p-6 text-slate-800 relative" style={{ fontSize: 12, width: landscape ? 1040 : undefined, maxWidth: "100%" }} data-orientation={landscape ? "landscape" : "portrait"}>
      {!H("Hide marksheet header") && (
        <div className="pb-2 mb-3 flex items-center justify-center gap-3 rounded-t" style={{ borderBottom: "2px solid #1e293b", ...(style.header_bg ? { ...headStyle, padding: 10, borderBottom: "none" } : {}) }}>
          {school?.logo && <img src={school.logo} alt="logo" className="object-contain shrink-0" style={{ width: num(style.logo_width) || 56, height: num(style.logo_width) || 56 }} />}
          <div className="text-center">
            <h2 className="font-extrabold tracking-tight" style={{ fontSize: num(style.school_name_fs) || 20 }}>{school?.name || "SSSPS"}</h2>
            {school?.tag_line && <p className="italic" style={{ fontSize: num(style.tagline_fs) || 11, opacity: 0.85 }}>{school.tag_line}</p>}
            {school?.address && <p style={{ fontSize: num(style.details_fs) || 10, opacity: 0.8 }}>{school.address}</p>}
            <p className="font-semibold mt-1 tracking-wide" style={{ fontSize: num(style.headings_fs) || 14 }}>{L("Report Card Title", "PROGRESS REPORT")}</p>
          </div>
          {school?.marksheet_qr && <img src={school.marksheet_qr} alt="QR" className="object-contain shrink-0" style={{ width: num(style.qr_width) || 56, height: num(style.qr_width) || 56 }} />}
        </div>
      )}

      <div className="grid grid-cols-3 gap-x-5 gap-y-1 text-xs mb-3" style={{ fontSize: num(style.student_fs) || undefined }}>
        <div className="flex gap-1"><b>{L("Subject", "Name")}:</b>{field(st.name, (v) => update((c) => { c.student.name = v; }))}</div>
        <div className="flex gap-1"><b>{L("Class", "Class")}:</b><span>{st.class_name} {st.section}</span></div>
        <div className="flex gap-1"><b>Roll:</b>{field(st.roll_number, (v) => update((c) => { c.student.roll_number = v; }))}</div>
        <div className="flex gap-1"><b>Adm No:</b><span>{st.admission_number}</span></div>
        <div className="flex gap-1"><b>Father:</b>{field(st.father_name, (v) => update((c) => { c.student.father_name = v; }))}</div>
        <div className="flex gap-1"><b>Mother:</b>{field(st.mother_name, (v) => update((c) => { c.student.mother_name = v; }))}</div>
      </div>

      <div className="text-[11px] font-semibold text-slate-600 mb-1">{L("Academic Text", "Scholastic Areas")}</div>
      <table className="w-full text-xs border border-slate-400 border-collapse mb-3">
        <thead>
          <tr style={theadStyle}>
            <th className="border border-slate-300 px-2 py-1 text-left">{L("Subject", "Subject")}</th>
            {terms.map((t) => <th key={t} className="border border-slate-300 px-1 py-1 text-center">{H("Hide Group/Term Name From Exam's Top") ? "" : t}</th>)}
            {showTotal && <th className="border border-slate-300 px-1 py-1 text-center">{L("Total", "Total")}</th>}
            {showGrade && <th className="border border-slate-300 px-1 py-1 text-center">{L("Grade Label", "Grade")}</th>}
          </tr>
          {!H("Hide max marks from Exam header") && (
            <tr className="bg-slate-50 text-[9px] text-slate-400">
              <th className="border border-slate-300 px-2 py-0.5 text-left">Max →</th>
              {terms.map((t) => {
                const anyRow = (rpt.subjects || []).find((s) => s.terms?.[t]?.max);
                return <th key={t} className="border border-slate-300 px-1 py-0.5 text-center">{anyRow?.terms?.[t]?.max || "—"}</th>;
              })}
              {showTotal && <th className="border border-slate-300"></th>}
              {showGrade && <th className="border border-slate-300"></th>}
            </tr>
          )}
        </thead>
        <tbody>
          {(rpt.subjects || []).map((s, si) => (
            <tr key={s.subject_id}>
              <td className="border border-slate-300 px-2 py-1 font-medium">{s.subject_name}</td>
              {terms.map((t) => {
                const cell = (s.terms && s.terms[t]) || {};
                return (
                  <td key={t} className="border border-slate-300 px-1 py-0.5 text-center">
                    {cell.max === "" || cell.max == null
                      ? <span className="text-slate-300">—</span>
                      : field(cell.obtained, (v) => update((c) => { c.subjects[si].terms[t].obtained = v; }), "w-14")}
                  </td>
                );
              })}
              {showTotal && <td className="border border-slate-300 px-1 py-0.5 text-center font-semibold">{s.total_obtained}/{s.total_max}</td>}
              {showGrade && <td className="border border-slate-300 px-1 py-0.5 text-center font-bold">{s.grade}</td>}
            </tr>
          ))}
          {!H("Hide Overall Results Row") && (
            <tr className="bg-slate-50 font-semibold">
              <td className="border border-slate-300 px-2 py-1">{L("Subject Group Total", "Grand Total")}</td>
              <td className="border border-slate-300 px-1 py-1 text-center" colSpan={terms.length}>{L("Percentage", "Percentage")}: {rpt.percentage}%</td>
              {showTotal && <td className="border border-slate-300 px-1 py-1 text-center">{rpt.total_obtained}/{rpt.total_max}</td>}
              {showGrade && <td className="border border-slate-300 px-1 py-1 text-center">{rpt.overall_grade}</td>}
            </tr>
          )}
        </tbody>
      </table>

      <div className="grid grid-cols-2 gap-4">
        {!H("Hide Co-Scholastic Areas") && (
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1">{L("Co-Scholastic Areas", "Co-Scholastic Areas")}</div>
            <table className="w-full text-xs border border-slate-400 border-collapse">
              <thead><tr style={theadStyle}>
                <th className="border border-slate-300 px-2 py-1 text-left">Area</th>
                <th className="border border-slate-300 px-2 py-1 text-center w-20">{L("Grade Label", "Grade")}</th>
              </tr></thead>
              <tbody>
                {(rpt.coscholastic || []).map((co, ci) => (
                  <tr key={ci}>
                    <td className="border border-slate-300 px-2 py-1">{co.area}</td>
                    <td className="border border-slate-300 px-1 py-0.5 text-center">
                      {field(co.grade, (v) => update((c) => { c.coscholastic[ci].grade = v; }))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="space-y-2">
          <div className="text-[11px] font-semibold text-slate-600 mb-1">{L("Attendance Label", "Attendance")} &amp; {L("Result", "Result")}</div>
          <table className="w-full text-xs border border-slate-400 border-collapse">
            <tbody>
              {!H("Hide Attendance") && !H("Hide attendance total open/present day") && (<>
                <tr><td className="border border-slate-300 px-2 py-1">{L("Total Working Days", "Total Working Days")}</td>
                  <td className="border border-slate-300 px-1 py-0.5 text-center w-24">{field(att.working_days, (v) => update((c) => { c.attendance.working_days = v; }))}</td></tr>
                <tr><td className="border border-slate-300 px-2 py-1">{L("Total Present Days", "Days Present")}</td>
                  <td className="border border-slate-300 px-1 py-0.5 text-center">{field(att.present, (v) => update((c) => { c.attendance.present = v; }))}</td></tr>
              </>)}
              {!H("Hide Attendance") && (
                <tr><td className="border border-slate-300 px-2 py-1">{L("Attendance (%)", "Attendance %")}</td>
                  <td className="border border-slate-300 px-2 py-1 text-center font-semibold">{pct}%</td></tr>
              )}
              {!H("Hide overall rank") && (
                <tr><td className="border border-slate-300 px-2 py-1">{L("Rank", "Class Rank")}</td>
                  <td className="border border-slate-300 px-2 py-1 text-center font-semibold">{rpt.rank} / {rpt.class_size}</td></tr>
              )}
              {!H("Hide division") && division && (
                <tr><td className="border border-slate-300 px-2 py-1">{L("Division", "Division")}</td>
                  <td className="border border-slate-300 px-2 py-1 text-center font-semibold">{division}</td></tr>
              )}
              {!H("Hide Result/Percentage Column") && (
                <tr><td className="border border-slate-300 px-2 py-1">{L("Result", "Result")}</td>
                  <td className="border border-slate-300 px-2 py-1 text-center font-bold">{remark || (rpt.percentage >= 33 ? "PASS" : "NEEDS IMPROVEMENT")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!H("Hide teacher remark") && (
        <div className="mt-3">
          <div className="text-[11px] font-semibold text-slate-600 mb-1">{L("Teacher Remark Label", "Class Teacher's Remarks")}</div>
          {editable
            ? <textarea className="w-full border border-slate-300 rounded px-2 py-1 text-xs" rows={2} value={rpt.remarks || ""} onChange={(e) => update((c) => { c.remarks = e.target.value; })} />
            : <div className="border border-slate-300 rounded px-2 py-2 text-xs min-h-[36px]">{rpt.remarks}</div>}
        </div>
      )}

      {!H("Hide Grade Range Row") && (
        <div className="mt-2 text-[9px] text-slate-500">
          <b>{L("Grade Range", "Grading Scale")}:</b> {(grades || []).map((g) => `${g.grade} (${g.min}-${g.max})`).join("  ·  ")}
        </div>
      )}
      {!H("Hide Percentage Range Row") && (remarks || []).length > 0 && (
        <div className="mt-1 text-[9px] text-slate-500">
          <b>{L("Percentage Range", "Remarks Scale")}:</b> {(remarks || []).map((r) => `${r.remark} (${r.min}-${r.max})`).join("  ·  ")}
        </div>
      )}

      <div className="flex justify-between items-end mt-8 text-xs text-slate-500">
        {!H("Hide class teacher's sign") ? <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">{L("Teacher Sign", "Class Teacher")}</div></div> : <div />}
        {!H("Hide date") && <div className="text-center text-[10px]">{L("Marksheet Date", "Date")}: ______</div>}
        {!H("Hide principal's sign") ? <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">{L("Authority/Principal Sign", "Principal")}</div></div> : <div />}
      </div>
    </div>
  );
}

export default function ProgressCardMode({ school, exams, refreshSchool }) {
  const [classId, setClassId] = useState("");
  const [slots, setSlots] = useState(DEFAULT_TERMS.map((label) => ({ label, exam_id: "" })));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [card, setCard] = useState(null);
  const [edited, setEdited] = useState({});
  const [printAll, setPrintAll] = useState(false);
  // settings
  const [setOpen, setSetOpen] = useState(false);
  const [savingSet, setSavingSet] = useState(false);
  const [sGrades, setSGrades] = useState([]);
  const [coAreas, setCoAreas] = useState([]);
  const [newArea, setNewArea] = useState("");

  const classes = useMemo(() => Array.from(new Map(exams.map((e) => [e.class_id, e.class_name])).entries()), [exams]);
  const classExams = exams.filter((e) => e.class_id === classId);
  const terms = data?.terms || [];
  const grades = data?.scholastic_grades || DEFAULT_SCHOLASTIC;

  useEffect(() => { setData(null); setEdited({}); setSlots(DEFAULT_TERMS.map((label) => ({ label, exam_id: "" }))); }, [classId]);

  const updSlot = (i, exam_id) => setSlots((p) => p.map((s, idx) => idx === i ? { ...s, exam_id } : s));

  const generate = async () => {
    const mapped = slots.filter((s) => s.exam_id);
    if (!classId) { toast.error("Select a class"); return; }
    if (!mapped.length) { toast.error("Map at least one term to an exam"); return; }
    setLoading(true); setEdited({});
    try {
      const r = await api.post("/reports/progress-card", { class_id: classId, terms: mapped });
      setData(r.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const reports = data?.reports || [];
  const current = (r) => recalc(edited[r.student.id] || r, data.terms, grades);
  const filtered = reports.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.student.name, r.student.admission_number, r.student.roll_number].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const openCard = (r) => setCard(recalc(edited[r.student.id] || r, data.terms, grades));
  const updateCard = (mutator) => setCard((prev) => { const c = structuredClone(prev); mutator(c); return recalc(c, data.terms, grades); });
  const saveCard = () => { setEdited((m) => ({ ...m, [card.student.id]: card })); toast.success("Saved edits for this student"); setCard(null); };
  const printSingle = () => { document.body.classList.remove("printing-bulk"); window.print(); };
  const printAllCards = () => {
    if (!reports.length) return;
    setPrintAll(true);
    document.body.classList.add("printing-bulk");
    setTimeout(() => window.print(), 200);
    const cleanup = () => { document.body.classList.remove("printing-bulk"); setPrintAll(false); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 3000);
  };

  // ---- settings ----
  const openSettings = () => {
    setSGrades(school?.scholastic_grades?.length ? structuredClone(school.scholastic_grades) : structuredClone(DEFAULT_SCHOLASTIC));
    setCoAreas(school?.coscholastic_areas?.length ? [...school.coscholastic_areas] : [...DEFAULT_CO_AREAS]);
    setSetOpen(true);
  };
  const saveSettings = async () => {
    setSavingSet(true);
    try {
      await api.put("/settings", { ...school, scholastic_grades: sGrades, coscholastic_areas: coAreas });
      await refreshSchool?.();
      toast.success("Grading settings saved");
      setSetOpen(false);
    } catch (e) { toast.error("Could not save settings"); }
    finally { setSavingSet(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="progress-controls">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Class</label>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger className="w-56 mt-1" data-testid="pc-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classes.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="gap-1.5 ml-auto" onClick={openSettings} data-testid="pc-settings-btn"><Settings2 size={15} />Grading & Areas</Button>
        </div>
        {classId && (
          <div>
            <label className="text-xs text-slate-500 font-medium">Map each term to an exam</label>
            <div className="mt-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {slots.map((s, i) => (
                <div key={s.label} className="border border-slate-200 rounded-lg p-2">
                  <div className="text-xs font-semibold text-slate-700 mb-1">{s.label}</div>
                  <Select value={s.exam_id} onValueChange={(v) => updSlot(i, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`pc-term-${i}`}><SelectValue placeholder="— none —" /></SelectTrigger>
                    <SelectContent>
                      {classExams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Button onClick={generate} disabled={!classId} data-testid="pc-generate-btn" className="gap-1.5"><GraduationCap size={15} />Generate Progress Cards</Button>
          {data && reports.length > 0 && <Button variant="outline" className="gap-1.5" onClick={printAllCards} data-testid="pc-print-all"><Printer size={15} />Print All ({reports.length})</Button>}
          {data && (
            <div className="relative ml-auto w-64">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder="Search students…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pc-search" />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          !data ? <div className="py-16 text-center text-slate-400">Select a class, map terms and click Generate.</div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400">No students found.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Student</th>
                  <th className="text-left px-4 py-3">Adm No.</th>
                  <th className="text-left px-4 py-3">Rank</th>
                  <th className="text-left px-4 py-3">Total</th>
                  <th className="text-left px-4 py-3">%</th>
                  <th className="text-left px-4 py-3">Grade</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r0) => {
                  const r = current(r0);
                  const dirty = !!edited[r.student.id];
                  return (
                    <tr key={r.student.id} className="hover:bg-slate-50" data-testid={`pc-row-${r.student.admission_number}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-800">{r.student.name}{dirty && <span className="ml-2 text-[10px] text-amber-600">edited</span>}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.student.admission_number}</td>
                      <td className="px-4 py-2.5 font-semibold">{r.rank <= 3 ? <span className="inline-flex items-center gap-1 text-amber-600"><Trophy size={13} />{r.rank}</span> : r.rank}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.total_obtained}/{r.total_max}</td>
                      <td className="px-4 py-2.5">{r.percentage}%</td>
                      <td className="px-4 py-2.5 font-bold">{r.overall_grade}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openCard(r0)} data-testid={`pc-open-${r.student.admission_number}`}><Eye size={14} />Preview / Edit</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      <Dialog open={!!card} onOpenChange={(o) => !o && setCard(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="pc-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Progress Card Preview</DialogTitle></DialogHeader>
          <div className="print-area">{card && <ProgressCardView rpt={card} terms={data.terms} school={data.school} grades={grades} coGrades={data.coscholastic_grades} adv={data.advanced} divisions={data.divisions} remarks={data.percentage_remarks} editable update={updateCard} />}</div>
          <div className="flex justify-end gap-2 p-4 border-t border-slate-100 no-print">
            <Button variant="outline" onClick={() => setCard(null)}>Close</Button>
            <Button variant="outline" onClick={saveCard} className="gap-1.5" data-testid="pc-save-edits"><Save size={15} />Save Edits</Button>
            <Button onClick={printSingle} className="gap-1.5" data-testid="pc-print-single"><Printer size={15} />Print</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pc-settings-dialog">
          <DialogHeader><DialogTitle>Grading Scale & Co-Scholastic Areas</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Scholastic Grade Bands</Label>
                <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setSGrades((p) => [...p, { min: 0, max: 0, grade: "" }])} data-testid="pc-add-band"><Plus size={13} />Add band</Button>
              </div>
              <div className="space-y-1.5">
                {sGrades.map((g, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`pc-band-${i}`}>
                    <Input type="number" className="h-8 w-20" value={g.min} onChange={(e) => setSGrades((p) => p.map((x, idx) => idx === i ? { ...x, min: e.target.value } : x))} placeholder="Min" />
                    <span className="text-slate-400">–</span>
                    <Input type="number" className="h-8 w-20" value={g.max} onChange={(e) => setSGrades((p) => p.map((x, idx) => idx === i ? { ...x, max: e.target.value } : x))} placeholder="Max" />
                    <Input className="h-8 w-24" value={g.grade} onChange={(e) => setSGrades((p) => p.map((x, idx) => idx === i ? { ...x, grade: e.target.value } : x))} placeholder="Grade" />
                    <button onClick={() => setSGrades((p) => p.filter((_, idx) => idx !== i))} className="text-rose-400 hover:text-rose-600"><XIcon size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Co-Scholastic Areas</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {coAreas.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-3 py-1 text-xs" data-testid={`pc-area-${i}`}>
                    {a}<button onClick={() => setCoAreas((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-500"><XIcon size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input className="h-8" value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="Add an area…" onKeyDown={(e) => { if (e.key === "Enter" && newArea.trim()) { setCoAreas((p) => [...p, newArea.trim()]); setNewArea(""); } }} data-testid="pc-new-area" />
                <Button size="sm" variant="outline" onClick={() => { if (newArea.trim()) { setCoAreas((p) => [...p, newArea.trim()]); setNewArea(""); } }}>Add</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSetOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingSet} className="gap-1.5" data-testid="pc-save-settings">{savingSet ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {data && reports.length > 0 && printAll && (
        <div className="print-area-bulk">
          {filtered.map((r0) => {
            const r = current(r0);
            return <div key={r.student.id} className="report-card-page"><ProgressCardView rpt={r} terms={data.terms} school={data.school} grades={grades} coGrades={data.coscholastic_grades} adv={data.advanced} divisions={data.divisions} remarks={data.percentage_remarks} /></div>;
          })}
        </div>
      )}
    </div>
  );
}
